/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React from 'react';
let loggedInitialDeprecationWarning = false;

/* eslint-disable no-console */

function getRenderStack() {
  let renderStack = '';
  try {
    // this is so bad to use, that its not even typed :)
    renderStack = React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactDebugCurrentFrame.getStackAddendum();
  } catch (error) {
    // log happened outside a react render or couldn't figure out where in the render stack we are.
  }
  return renderStack;
}

/**
 * Logs a message to the console if not in Production and the condition param is false.
 * @param level The message level
 * @param withRenderStack Whether to log the React render stack
 * @param condition if true this method is skipped
 * @param message the message to log
 * @param args any extra arguments to be passed to the console.
 */
function logMessage(level, withRenderStack, condition, message) {
  if (process.env.NODE_ENV !== 'production' && !condition) {
    if (typeof console[level] === 'function') {
      const renderStack = withRenderStack ? getRenderStack() : '';
      //@ts-expect-error level can be 'constructor' which is not callable
      for (var _len = arguments.length, args = new Array(_len > 4 ? _len - 4 : 0), _key = 4; _key < _len; _key++) {
        args[_key - 4] = arguments[_key];
      }
      console[level](`Warning: ${message}`, ...args, renderStack);
    } else {
      throw new Error(`'${level}' is not a valid console method!`);
    }
  }
}
function logDeprecated(condition, message) {
  if (!process.env.OMIT_INSTUI_DEPRECATION_WARNINGS) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
      args[_key2 - 2] = arguments[_key2];
    }
    logMessage('warn', true, condition, message, ...args);
  } else if (!condition && !loggedInitialDeprecationWarning) {
    loggedInitialDeprecationWarning = true;
    logMessage('warn', false, condition, ['There are Instructure UI deprecation warnings that are being hidden because the `OMIT_INSTUI_DEPRECATION_WARNINGS` environment variable is set. Remove or unset this variable to see the full list of warnings in your console.', 'These warnings will give you advance notice of breaking changes and upgrade guidance to keep your code up to date with the latest Instructure UI versions.'].join('\n\n'));
  }
}
export const error = function (condition, message) {
  for (var _len3 = arguments.length, args = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
    args[_key3 - 2] = arguments[_key3];
  }
  return logMessage('error', true, condition, message, ...args);
};
export const warn = function (condition, message) {
  for (var _len4 = arguments.length, args = new Array(_len4 > 2 ? _len4 - 2 : 0), _key4 = 2; _key4 < _len4; _key4++) {
    args[_key4 - 2] = arguments[_key4];
  }
  return logMessage('warn', true, condition, message, ...args);
};
export const warnDeprecated = function (condition, message) {
  for (var _len5 = arguments.length, args = new Array(_len5 > 2 ? _len5 - 2 : 0), _key5 = 2; _key5 < _len5; _key5++) {
    args[_key5 - 2] = arguments[_key5];
  }
  return logDeprecated(condition, message, ...args);
};
export const info = function () {
  return console.info(...arguments);
};
export const assert = function (condition) {
  for (var _len6 = arguments.length, data = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
    data[_key6 - 1] = arguments[_key6];
  }
  return console.assert(condition, data);
};
export const debug = function (message) {
  for (var _len7 = arguments.length, optionalParams = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
    optionalParams[_key7 - 1] = arguments[_key7];
  }
  return console.debug(message, optionalParams);
};
export const log = function (message) {
  for (var _len8 = arguments.length, optionalParams = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
    optionalParams[_key8 - 1] = arguments[_key8];
  }
  return console.log(message, optionalParams);
};

/* eslint-enable no-console */